import { ChartOptions } from "chart.js";
import { useEffect, useMemo } from "react";

import scatterConfig from "./Config";

import useStore from "../../../../../store";
import { ChartType } from "../../../../../store/enums";

import { useGetGridDataCount, useGetRawReviewReportData } from "../../../../../../../api/hooks";
import useScatterChart, { IScatterPoint, UseScatterChartProps } from "./useScatterChart";

import avatarURL from "../../../../../../../../../utils/avatarURL";
import { formatSummarizeByAndAggregationOperations, getColumnKeyFromColumnTypeValue } from "../../../../../utils";
import { inferMinMax } from "../utils";
import { getPointTypeFromDataseries } from "./utils";

import Config from "../../../../../Config";
import OptionSettings from "../../shared/OptionSettings";
import { rawReviewReportAvatarsExtractor } from "../../shared/avatarExtractor";
import useChartOptions from "../UseChartOptions/useChartOptions";
import usePreloadImages from "../preloadImages";

const DEFAULT_MIN = 0;
const DEFAULT_MAX = 100;

type UseScatterChartDataProps = {
	chartType: ChartType;
	color?: string;
	backgroundColor?: string;
	activeBorderColor?: string;
	xAxisLabel?: string;
	yAxisLabel?: string;
	scaleOptions: Partial<ChartOptions<"scatter">["scales"]>;
	padding?: { left?: number; right?: number; top?: number; bottom?: number };
};

/**
 * A hook to reference the selected options, retreive and format the data
 * for the nine box chart
 */
const useScatterChartData = ({
	chartType,
	color = undefined,
	backgroundColor = undefined,
	activeBorderColor = undefined,
	xAxisLabel = undefined,
	yAxisLabel = undefined,
	scaleOptions = {},
	padding = {
		left: 48,
		bottom: 56,
		right: 16,
		top: 24,
	},
}: UseScatterChartDataProps) => {
	const fetchedVariables: string[] = [];
	// store methods and state
	const [
		enabledSummarizeFields,
		selectedChart,
		selectedQuickFilter,
		getAppliedFilters,
		getSummaryColumnMetadata,
		getEnabledGridColumns,
		report,
		canViewSummary,
	] = useStore((state) => [
		state.enabledSummarizeFields,
		state.selectedChart,
		state.selectedQuickFilter,
		state.getAppliedFilters,
		state.getSummaryColumnMetadata,
		state.getEnabledGridColumns,
		state.report,
		state.canViewSummary,
	]);

	// selected chart options
	const {
		visualizeOption: chartOptions,
		setMinMaxXYCallback,
		getSortSettings,
		selectableSortSettings,
		getAxisOptionsAsColumns,
		anyAxisOptions,
	} = useChartOptions(chartType);

	const filters = useMemo(() => {
		return getAppliedFilters();
	}, [selectedQuickFilter, JSON.stringify(report)]);

	const { variables, prefetchVariables, nameKeyOne, nameKeyTwo, type, aggregationOperations } = useMemo(() => {
		const dataSeries = chartOptions?.dataSeries?.column.split("_");
		const type = getPointTypeFromDataseries(dataSeries);
		const nameKeyOne = dataSeries ? Config.fieldGroupLookup[dataSeries[1]] : "";
		const nameKeyTwo = dataSeries?.splice(2).join("_") || "";

		const summaryFunctions = getSummaryColumnMetadata();
		const { summarizeBy, aggregationOperations } = formatSummarizeByAndAggregationOperations(
			enabledSummarizeFields,
			summaryFunctions
		);

		const pageSize = (chartOptions?.dataSeries?.rangeOption || scatterConfig.DEFAULT_RANGE_OPTION).split("_")[1];

		const sorts = getSortSettings();
		const columns = getAxisOptionsAsColumns();

		// we only summarize by one field at a time in the nine box chart
		const summarizeBySelection = summarizeBy.find((s) => s.type === nameKeyOne && s.name === nameKeyTwo) || [];

		fetchedVariables.push(
			JSON.stringify({
				pageSize,
				sortDirection: sorts[0]?.direction || "desc",
			})
		);

		const variables = {
			questionGroupingSessionID: report?.questionGroupingSessionID,
			columns,
			filters: filters,
			pageNumber: "1",
			pageSize,
			sorts,
			summarizeBy: summarizeBySelection,
			aggregationOperations,
		};

		const prefetchVariables: (typeof variables)[] = [];
		for (let i = 0; i < selectableSortSettings.length; i++) {
			for (let j = 0; j < OptionSettings[chartType].dataSeriesRangeOptions.length; j++) {
				const newVariables = {
					...variables,
					sorts: selectableSortSettings[i],
					pageSize: OptionSettings[chartType].dataSeriesRangeOptions[j].value.split("_")[1],
				};
				// if this variables configuration is new, prefetch the data
				if (
					!fetchedVariables.includes(
						JSON.stringify({
							pageSize: newVariables.pageSize,
							sortDirection: newVariables.sorts[0].direction,
						})
					)
				) {
					prefetchVariables.push(newVariables);
					fetchedVariables.push(
						JSON.stringify({
							pageSize: newVariables.pageSize,
							sortDirection: newVariables.sorts[0].direction,
						})
					);
				}
			}
		}

		// set up for the api call
		return {
			variables,
			prefetchVariables,
			nameKeyOne,
			nameKeyTwo,
			type,
			aggregationOperations,
		};
	}, [enabledSummarizeFields, JSON.stringify(chartOptions), JSON.stringify(filters), JSON.stringify(report)]);

	// api calls
	const results = useGetRawReviewReportData([variables, ...prefetchVariables], canViewSummary, report?.type);
	const { data, isLoading } = results[0];

	const avatarUrls = useMemo(() => rawReviewReportAvatarsExtractor(data, type, nameKeyOne, nameKeyTwo), [data]);
	const { imagesLoaded } = usePreloadImages(avatarUrls, isLoading);
	const { data: countData } = useGetGridDataCount(variables, report?.type);

	const {
		scatterChartData = [],
		minX,
		maxX,
		minY,
		maxY,
	} = useMemo(() => {
		const keyMap = {
			reviewReport: {
				employee: "subject",
				question: "questions_map",
				review: "review",
				reviewcycle: "review_cycle",
				reviewreport: "review_report",
				reviewform: "review_form",
				groupedquestion: "question_group_map",
			},
			surveyReport: {
				survey_report: "survey_report",
				survey: "survey",
				groupedquestion: "question_group_map",
				question: "questions_map",
				employee: "participant",
			},
			goalReport: {
				goal: "goal",
				employee: "assignee",
			},
			employeeReport: {
				employee: "subject",
			},
		};

		const visualizationDataKey = {
			reviewReport: "reviewReportsVisualization",
			surveyReport: "surveyReportsVisualization",
			goalReport: "goalReportsVisualization",
			employeeReport: "employeeReportsVisualization",
		};

		let miniX, miniY, maxiX, maxiY; // extract min and max values from data for scaling
		const bothColumnsSelected = chartOptions?.xAxis?.column?.length && chartOptions?.yAxis?.column?.length;

		if (isLoading || selectedChart !== chartType || !bothColumnsSelected || !data) {
			return [];
		}

		const dataKey = visualizationDataKey[report?.type || "reviewReport"];
		const visualizationData = data[dataKey];

		if (!visualizationData) {
			return [];
		}

		const formattedData: IScatterPoint[] = visualizationData.map((row) => {
			let name = row.summarizeBy[nameKeyOne][nameKeyTwo]?.name;

			if (!name && nameKeyTwo === "Manager") {
				name = "No Manager";
			}

			if (row["subject"] && row["subject"]["custom_values"]) {
				row["subject"] = { ...row["subject"], ...row["subject"]["custom_values"] };
			}
			if (row["assignee"] && row["assignee"]["custom_values"]) {
				row["assignee"] = { ...row["assignee"], ...row["assignee"]["custom_values"] };
			}
			if (row["participant"] && row["participant"]["custom_values"]) {
				row["participant"] = { ...row["participant"], ...row["participant"]["custom_values"] };
			}

			/**
			 * Extracts the data key and field name from a column ID
			 * @param column - Column ID like "survey_participant_questions_answered"
			 * @returns Object with dataKey and fieldName
			 *
			 * Examples:
			 * - "survey_participant_questions_answered" -> { dataKey: "survey_report", fieldName: "participant_questions_answered" }
			 * - "review_report_status" -> { dataKey: "reviewreport", fieldName: "status" }
			 * - "employee_Manager" -> { dataKey: "employee", fieldName: "Manager" }
			 */
			const parseColumnId = (column: string): { dataKey: string; fieldName: string } => {
				// Handle multi-part prefixes first (these need to match keyMap keys)
				if (column.startsWith("review_cycle_")) {
					return { dataKey: "reviewcycle", fieldName: column.replace("review_cycle_", "") };
				}
				if (column.startsWith("review_report_")) {
					return { dataKey: "reviewreport", fieldName: column.replace("review_report_", "") };
				}
				if (column.startsWith("review_form_")) {
					return { dataKey: "reviewform", fieldName: column.replace("review_form_", "") };
				}

				// For survey reports: "survey_X" maps to row["survey_report"]["X"]
				if (column.startsWith("survey_") && report?.type === "surveyReport") {
					return { dataKey: "survey_report", fieldName: column.replace("survey_", "") };
				}

				// Default: split on first underscore
				const firstUnderscoreIndex = column.indexOf("_");
				if (firstUnderscoreIndex === -1) {
					return { dataKey: column, fieldName: column };
				}

				const prefix = column.slice(0, firstUnderscoreIndex);
				const suffix = column.slice(firstUnderscoreIndex + 1);
				return { dataKey: prefix, fieldName: suffix };
			};

			const getValue = (key: string, rowData: any, columnId: string = "") => {
				if (key in keyMap[report?.type || "reviewReport"] && typeof rowData === "object") {
					return rowData?.answer;
				}

				const reportDataKey = keyMap[report?.type || "reviewReport"][key] || "review_report";

				if ("reviewform_authors" === columnId) {
					return row[reportDataKey]["authors"];
				}

				if ("reviewform_signers" === columnId) {
					return row[reportDataKey]["signers"];
				}

				if ("reviewform_status" === columnId) {
					return row[reportDataKey]["status"];
				}

				return rowData;
			};

			const xColumn = chartOptions?.xAxis?.column || "";
			const yColumn = chartOptions?.yAxis?.column || "";

			const { dataKey: xDataKey, fieldName: xFieldName } = parseColumnId(xColumn);
			const xData = row[keyMap[report?.type || "reviewReport"][xDataKey]]?.[xFieldName] || row?.[xFieldName];
			const x = getValue(xDataKey, xData, xColumn);

			const { dataKey: yDataKey, fieldName: yFieldName } = parseColumnId(yColumn);
			const yData = row[keyMap[report?.type || "reviewReport"][yDataKey]]?.[yFieldName] || row?.[yFieldName];
			const y = getValue(yDataKey, yData, yColumn);

			const avatar =
				type === "employee" && row.summarizeBy[nameKeyOne][nameKeyTwo]?._id
					? avatarURL(row.summarizeBy[nameKeyOne][nameKeyTwo]._id)
					: null;

			if (typeof x !== "undefined" && x !== null) {
				miniX = typeof miniX === "undefined" ? x : Math.min(miniX, x);
				maxiX = typeof maxiX === "undefined" ? x : Math.max(maxiX, x);
			}

			if (typeof y !== "undefined" && y !== null) {
				miniY = typeof miniY === "undefined" ? y : Math.min(miniY, y);
				maxiY = typeof maxiY === "undefined" ? y : Math.max(maxiY, y);
			}

			return {
				x,
				y,
				name,
				type,
				avatar,
				color,
				backgroundColor,
				activeBorderColor,
			};
		});

		// infer min and max values for x and y axis
		const [adjustedMinX, adjustedMaxX] = inferMinMax(miniX, maxiX);
		const [adjustedMinY, adjustedMaxY] = inferMinMax(miniY, maxiY);

		return {
			scatterChartData: formattedData,
			minX: adjustedMinX,
			maxX: adjustedMaxX,
			minY: adjustedMinY,
			maxY: adjustedMaxY,
		};
	}, [
		JSON.stringify(chartOptions),
		type,
		nameKeyOne,
		nameKeyTwo,
		JSON.stringify(data),
		isLoading,
		selectedChart,
	]) as {
		scatterChartData: IScatterPoint[];
		minX: number;
		maxX: number;
		minY: number;
		maxY: number;
	};

	const xLabel =
		xAxisLabel || anyAxisOptions.find((column) => column.value === chartOptions?.xAxis?.column)?.label || "X Axis";
	const yLabel =
		yAxisLabel || anyAxisOptions.find((column) => column.value === chartOptions?.yAxis?.column)?.label || "Y Axis";

	const xAggregateLabel = xAxisLabel
		? undefined // only get aggregate labels if label is not provided
		: anyAxisOptions.find((column) => column.value === chartOptions?.xAxis?.column)?.fixedText || undefined;
	const yAggregateLabel = yAxisLabel
		? undefined
		: anyAxisOptions.find((column) => column.value === chartOptions?.yAxis?.column)?.fixedText || undefined;

	useEffect(() => {
		setMinMaxXYCallback(
			minX === 0 ? minX : minX || DEFAULT_MIN,
			maxX === 0 ? maxX : maxX || DEFAULT_MAX,
			minY === 0 ? minY : minY || DEFAULT_MIN,
			maxY === 0 ? maxY : maxY || DEFAULT_MAX
		);
	}, [chartOptions?.xAxis?.column, chartOptions?.yAxis?.column, minX, maxX, minY, maxY]);

	const [fieldTypeValueX, fieldValueX] = chartOptions?.xAxis?.column?.split("_") || [];
	const [fieldTypeValueY, fieldValueY] = chartOptions?.yAxis?.column?.split("_") || [];

	const [minXValue, maxXValue, minYValue, maxYValue] = useMemo(() => {
		const gridData = getEnabledGridColumns();
		const suggestedMinX = gridData.find((column) => column.field === chartOptions?.xAxis?.column)?.columnMetadata
			?.min;
		const suggestedMaxX = gridData.find((column) => column.field === chartOptions?.xAxis?.column)?.columnMetadata
			?.max;
		const suggestedMinY = gridData.find((column) => column.field === chartOptions?.yAxis?.column)?.columnMetadata
			?.min;
		const suggestedMaxY = gridData.find((column) => column.field === chartOptions?.yAxis?.column)?.columnMetadata
			?.max;
		const minXValue = parseFloat(
			chartOptions?.xAxis?.min === 0 ? 0 : ((chartOptions?.xAxis?.min || suggestedMinX) ?? DEFAULT_MIN)
		);
		const maxXValue = parseFloat(
			chartOptions?.xAxis?.max === 0 ? 0 : ((chartOptions?.xAxis?.max || suggestedMaxX) ?? DEFAULT_MAX)
		);
		const minYValue = parseFloat(
			chartOptions?.yAxis?.min === 0 ? 0 : ((chartOptions?.yAxis?.min || suggestedMinY) ?? DEFAULT_MIN)
		);
		const maxYValue = parseFloat(
			chartOptions?.yAxis?.max === 0 ? 0 : ((chartOptions?.yAxis?.max || suggestedMaxY) ?? DEFAULT_MAX)
		);

		return [minXValue, maxXValue, minYValue, maxYValue];
	}, [isLoading, JSON.stringify(scatterChartData), JSON.stringify(chartOptions)]);

	const scatterChartProps: UseScatterChartProps = useMemo(() => {
		const filteredData = scatterChartData.filter(
			// be sure both points exist and are within bounds
			(point) => {
				return (
					(point.x || point.x === 0) &&
					(point.y || point.y === 0) &&
					// use Math.min to filter within actual bounds since axes can be reversed
					parseFloat(point.x.toFixed(2)) <= Math.max(maxXValue, minXValue) &&
					parseFloat(point.x.toFixed(2)) >= Math.min(maxXValue, minXValue) &&
					parseFloat(point.y.toFixed(2)) <= Math.max(maxYValue, minYValue) &&
					parseFloat(point.y.toFixed(2)) >= Math.min(minYValue, maxYValue)
				);
			}
		) as IScatterPoint[];
		return {
			axisLabels: {
				xAxisLabel: xLabel,
				xAggregateLabel: xAggregateLabel,
				yAxisLabel: yLabel,
				yAggregateLabel: yAggregateLabel,
				fontColor: "#102430",
				fontSize: 14,
			},
			summarizationX:
				aggregationOperations.find(
					(operation) =>
						operation.name === fieldValueX &&
						operation.type === getColumnKeyFromColumnTypeValue(fieldTypeValueX)
				)?.operation || "average",
			summarizationY:
				aggregationOperations.find(
					(operation) =>
						operation.name === fieldValueY &&
						operation.type === getColumnKeyFromColumnTypeValue(fieldTypeValueY)
				)?.operation || "average",
			diameter: 24,
			data: filteredData,
		};
	}, [
		JSON.stringify(scatterChartData),
		xLabel,
		xAggregateLabel,
		yLabel,
		yAggregateLabel,
		aggregationOperations,
		JSON.stringify(chartOptions),
		minXValue,
		maxXValue,
		minYValue,
		maxYValue,
	]);

	const { getScaledOptions, prepareDataForChart } = useScatterChart(scatterChartProps);

	const [chartData, scaledOptions] = useMemo(() => {
		const chartData = prepareDataForChart();
		const scaledOptions = getScaledOptions({
			padding,
			scales: {
				x: {
					reverse: minXValue > maxXValue,
					min: minXValue,
					max: maxXValue,
					...scaleOptions["x"],
					ticks: {
						...scaleOptions["x"]?.ticks,
						callback: (value: string | number) => {
							const numericValue = parseFloat(value.toString());
							return parseFloat(numericValue.toFixed(2));
						},
					},
				},
				y: {
					reverse: minYValue > maxYValue,
					min: minYValue,
					max: maxYValue,
					...scaleOptions["y"],
					ticks: {
						...scaleOptions["y"]?.ticks,
						callback: (value: string | number) => {
							const numericValue = parseFloat(value.toString());
							return parseFloat(numericValue.toFixed(2));
						},
					},
				},
			},
		});
		return [chartData, scaledOptions];
	}, [JSON.stringify(scatterChartProps), minXValue, maxXValue, minYValue, maxYValue]);

	return {
		scatterChartData: chartData,
		count: countData?.reviewReportsCount?.count,
		scaledOptions,
		axisLabels: {
			xAxisLabel: xLabel,
			xAggregateLabel,
			yAxisLabel: yLabel,
			yAggregateLabel,
		},
		isLoading: isLoading || !imagesLoaded,
	};
};

export default useScatterChartData;
